import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PhotoDropZone from '../components/PhotoDropZone';

function fmt(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function GalleryAlbum() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [album, setAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/gallery/albums/${id}`).then((r) => setAlbum(r.data)).catch(() => setAlbum(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (files) => {
    setUploading(true); setError('');
    const failures = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/api/gallery/albums/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        failures.push(`${file.name}: ${err.response?.data?.detail || 'Upload fehlgeschlagen'}`);
      }
    }
    if (failures.length) setError(failures.join(' · '));
    load();
    setUploading(false);
  };

  const deletePhoto = async (photoId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Foto wirklich löschen?')) return;
    try {
      await api.delete(`/api/gallery/photos/${photoId}`);
      setLightboxIndex(null);
      load();
    } catch { /* ignore */ }
  };

  const setCover = async (photoId, e) => {
    e?.stopPropagation();
    try {
      await api.put(`/api/gallery/albums/${id}/cover`, { photo_id: photoId });
      load();
    } catch { /* ignore */ }
  };

  const photos = album ? album.photos : [];

  const closeLightbox = () => setLightboxIndex(null);
  const showPrev = useCallback((e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNext = useCallback((e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, showPrev, showNext]);

  if (album === null) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" /></div>;
  }
  if (!album) {
    return (
      <div className="max-w-2xl mx-auto card text-center py-14">
        <p className="text-slate-400 text-sm mb-3">Album nicht gefunden.</p>
        <Link to="/galerie" className="text-sm font-semibold text-ocean-600 hover:text-ocean-800 no-underline">← Zur Galerie</Link>
      </div>
    );
  }

  const current = lightboxIndex !== null ? photos[lightboxIndex] : null;

  return (
    <div>
      <Link to="/galerie" className="text-sm text-ocean-600 hover:underline mb-4 inline-block no-underline">← Alle Alben</Link>
      <h1 className="page-title mb-6">📸 {album.name}</h1>

      {isAdmin && (
        <div className="card mb-6">
          {error && <div className="alert-error mb-3">{error}</div>}
          <PhotoDropZone onFiles={uploadFiles} disabled={uploading} label={uploading ? 'Lädt hoch…' : 'Bilder hierher ziehen oder klicken zum Hochladen'} />
        </div>
      )}

      {photos.length === 0 ? (
        <div className="card text-center py-14">
          <p className="text-slate-400 text-sm">Noch keine Bilder in diesem Album.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p, idx) => (
            <div key={p.id} className="relative group">
              <button
                onClick={() => setLightboxIndex(idx)}
                className="block w-full aspect-square overflow-hidden rounded-lg border border-slate-200 p-0 bg-slate-100 cursor-pointer"
              >
                <img src={p.photo_url} alt={p.caption || album.name} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" loading="lazy" />
              </button>
              {isAdmin && (
                <button
                  onClick={(e) => setCover(p.id, e)}
                  aria-label="Als Titelbild festlegen"
                  title={p.id === album.cover_photo_id ? 'Aktuelles Titelbild' : 'Als Titelbild festlegen'}
                  className={`absolute top-1.5 left-1.5 text-sm rounded-full w-6 h-6 flex items-center justify-center border-0 cursor-pointer ${
                    p.id === album.cover_photo_id ? 'bg-sand-500 text-white' : 'bg-black/60 hover:bg-black/80 text-white'
                  }`}
                >
                  ★
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={(e) => deletePhoto(p.id, e)}
                  aria-label="Foto löschen"
                  className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-0 cursor-pointer"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {current && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col" onClick={closeLightbox}>
          <div className="flex justify-center items-center py-3 relative shrink-0">
            <button
              onClick={closeLightbox}
              aria-label="Schließen"
              className="text-white/80 hover:text-white text-3xl leading-none bg-transparent border-0 cursor-pointer"
            >
              ×
            </button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center relative px-3" onClick={(e) => e.stopPropagation()}>
            {photos.length > 1 && (
              <button
                onClick={showPrev}
                aria-label="Vorheriges Bild"
                className="absolute left-1 sm:left-4 text-white/70 hover:text-white text-4xl leading-none bg-transparent border-0 cursor-pointer px-2 py-4 z-10"
              >
                ‹
              </button>
            )}
            <img src={current.photo_url} alt={current.caption || album.name} className="max-h-[75vh] max-w-[85vw] object-contain rounded-lg" />
            {photos.length > 1 && (
              <button
                onClick={showNext}
                aria-label="Nächstes Bild"
                className="absolute right-1 sm:right-4 text-white/70 hover:text-white text-4xl leading-none bg-transparent border-0 cursor-pointer px-2 py-4 z-10"
              >
                ›
              </button>
            )}
          </div>
          <div className="shrink-0 pb-4 pt-2 flex items-center justify-center gap-3 text-white/70 text-sm">
            <span>{current.uploader?.nickname || current.uploader?.username || '–'} · {fmt(current.created_at)}</span>
            {isAdmin && (
              current.id === album.cover_photo_id ? (
                <span className="text-sand-400 font-semibold">★ Titelbild</span>
              ) : (
                <button
                  onClick={() => setCover(current.id)}
                  className="text-sand-300 hover:text-sand-200 font-semibold bg-transparent border-0 cursor-pointer"
                >
                  Als Titelbild festlegen
                </button>
              )
            )}
            {isAdmin && (
              <button
                onClick={() => deletePhoto(current.id)}
                className="text-red-400 hover:text-red-300 font-semibold bg-transparent border-0 cursor-pointer"
              >
                Löschen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
